import { Injectable } from '@angular/core';

import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, filter, map } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipV2WinParticipantPick } from '../../../core/picks/betslip-v2-win-participant-pick';
import { isSingleSlipId } from '../../bet-generation/services/slip.utils';
import { ensureTabbedBetslip } from '../../linear/helpers';
import { ofTypeReplaceExistingPick } from '../../picks/helpers';
import { BetslipTypesActions } from '../../types/actions';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import BetslipErrorsActions from '../../validation/actions';
import BetslipSingleBetActions from '../actions';
import { singleBetStateSelector } from '../selectors';

@Injectable()
export default class StakeEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private userSettings: SyncUserSettingsService,
    ) {}

    setSingleBetStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setStake),
            concatLatestFrom(() => this.store.select(betslipCurrentTypeSelector)),
            filter(([_, currentType]) => currentType === BetslipType.Single),
            map(([action, _]) =>
                BetslipSingleBetActions.setSingleBetStake({
                    stake: action.stake,
                    silent: action.silent,
                    stakeStr: action.stakeStr,
                }),
            ),
        );
    });

    /**
     * When add new pick check if this pick has no stake set.
     * When no stake, check if all picks added have the same stake if so use it.
     */
    setDefaultStake$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetslipSingleBetActions.addPick),
            ensureTabbedBetslip(),
            concatLatestFrom(() => this.store.select(singleBetStateSelector)),
            concatMap(function* ([action, state]): Iterable<Action> {
                const pick = state.picks[action.pickId];
                if (pick && pick.stake == null) {
                    const singleBetPickStakes = Object.values(state.picks)
                        .map((sp) => sp.stake)
                        .filter((singleBetPickStake) => !!singleBetPickStake);
                    const defaultStake = effect.userSettings.bettingSettings.defaultStake || null;
                    const stake =
                        singleBetPickStakes.length > 0 &&
                        singleBetPickStakes.every((singleBetPickStake) => singleBetPickStake === singleBetPickStakes[0])
                            ? singleBetPickStakes[0]
                            : defaultStake;
                    yield BetslipSingleBetActions.setSingleBetPickStake({
                        pickId: action.pickId,
                        stake,
                        stakeStr: stake?.toString(),
                    });
                }
            }),
        );
    });

    setSingleBetPickEachwayForGolfPicks$ = createEffect(() => {
        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            //set each way to true when the pick is each way and the sport is golf
            filter(
                (action) =>
                    action.newPick instanceof BetslipV2WinParticipantPick &&
                    action.newPick.isEachWay &&
                    action.newPick.sportId === SportConstant.Golf,
            ),
            map((action) =>
                BetslipSingleBetActions.setSingleBetPickEachWay({
                    isEachWay: true,
                    pickId: action.newPick.id,
                }),
            ),
        );
    });

    setSingleBetStakeFromStakeHints$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.setStakeFromStakeHints),
            filter((stakeHints) => stakeHints.stakeHints.pickStakeHintInfos.length > 0),
            concatMap((stakeHints) => {
                const pickStakeHints = stakeHints.stakeHints.pickStakeHintInfos;

                // This will always be single-bet because only multi-singles return the stake hint on pick-level
                return pickStakeHints.map((hint) =>
                    BetslipSingleBetActions.setSingleBetPickStake({
                        silent: true,
                        stake: hint.newStakeHint,
                        pickId: hint.pickId,
                    }),
                );
            }),
        );
    });

    setSingleBetStakeFromStakeHint$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.setStakeFromSlipStakeHint),
            filter((stakeHint) => isSingleSlipId(stakeHint.slipStakeHint.slipId)),
            map(({ slipStakeHint }) =>
                BetslipSingleBetActions.setSingleBetStake({
                    stake: slipStakeHint.newStakeHint,
                    silent: true,
                }),
            ),
        );
    });
}
